import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import { getDeferredUser, isEmailValid, isPhoneValid, isUUIDValid } from 'shared-libs/helpers/utils'

import apis from 'browser/app/models/apis'
import { ISelectProps } from 'browser/components/atomic-elements/atoms/select'
import { SelectStyleProps } from 'browser/components/atomic-elements/atoms/select/select/interface'
// tslint:disable-next-line:max-line-length
import { SelectField } from 'browser/components/atomic-elements/molecules/fields/select-field'
import { EntityDataSource } from 'browser/components/atomic-elements/organisms/entity/entity-data-source'
import { MAX_UNGROUPED_SIZE, Query } from 'shared-libs/models/query'

/**
 * @uiComponent
 */
interface IRecipientsFieldProps extends ISelectProps {
  label?: string
  density?: string
}

interface IRecipientsFieldState {
  contacts: any[]
}

import type { Entity } from 'shared-libs/models/entity'

const USER_TYPE = '/1.0/entities/metadata/user.json'
const VALUE_COUNT_COMPUTATION = {
  countEntities: {
    path: 'uniqueId',
    type: 'valueCount',
  },
}

const normalizeRecipientValue = (value?: string | null): string | undefined => {
  if (_.isNil(value)) {
    return undefined
  }
  const candidate = String(value)
  if (isPhoneValid(candidate)) {
    const contact = getDeferredUser(candidate)
    return _.get(contact, 'phoneNumber.phone', candidate)
  }
  return candidate
}

const valuesMatch = (a?: string | null, b?: string | null): boolean => {
  if (a === b) {
    return true
  }
  const normalizedA = normalizeRecipientValue(a)
  const normalizedB = normalizeRecipientValue(b)
  return normalizedA != null && normalizedB != null && normalizedA === normalizedB
}

export class RecipientsField extends React.Component<IRecipientsFieldProps, IRecipientsFieldState> {

  public static defaultProps: Partial<IRecipientsFieldProps> = {
    label: 'Recipients',
  }

  private userDataSet: any
  private didInitDataset = false

  constructor(props: IRecipientsFieldProps) {
    super(props)
    this.state = {
      contacts: [],
    }
  }

  public componentDidMount() {
    this.initDataset()
  }

  public componentDidUpdate(prevProps: Readonly<IRecipientsFieldProps>): void {
    this.initDataset()
  }

  private initDataset() {
    if (this.props.isDisabled || this.didInitDataset) {
      return
    }

    this.didInitDataset = true
    
    this.userDataSet = new EntityDataSource({
      entityType: USER_TYPE,
    }).setOnChange((content: Array<{ data: Entity }>) => this.handleOnDatasetChange(content))

    new Query(apis).setComputations(VALUE_COUNT_COMPUTATION)
      .setEntityType(USER_TYPE)
      .setMetadata({ shouldIncludeLeafEntities: false })
      .setDebug({ context: 'input--RecipientsField' })
      .getCollection()
      .find()
      .then((content: { computations: { countEntities: number } }) => {
        const size = content.computations.countEntities
        this.userDataSet.handleMetadataChange({ size: Math.min(size, MAX_UNGROUPED_SIZE) })
      })
  }

  public componentWillUnmount() {
    this.userDataSet?.dispose()
  }

  public render() {
    return (
      <SelectField
        {...this.props}
        data-debug-id='recipientsField'
        isCreatable={true}
        isValidNewOption={this.handleValidate}
        multi={true}
        noResultsText=''
        onChange={this.handleChange}
        onInputChange={this.handleInputChange}
        options={this.getOptions()}
        placeholder='Enter names or email addresses or phone numbers'
        promptTextCreator={this.renderPrompt}
        styles={
          { 
          ...this.props.styles,
          placeholder: (base) => ({
            ...base,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            maxWidth: 'calc(100% - 36px)',
            overflow: 'hidden'
          })
        } as SelectStyleProps}
      />
    )
  }

  private handleOnDatasetChange = (content: Array<{ data: Entity }>) => {
    const suggestions =  _.map(content, (user) => {
      const entity = user.data.content
      const email = _.get(entity, 'person.emails[0].value', '')
      if (email) {
        return { label: `${user.data.displayName} (${email})`, value: email }
      }
      const phoneNumber = _.get(entity, 'person.phoneNumbers[0].value.phone', '')
      if (phoneNumber) {
        return { label: `${user.data.displayName} (${phoneNumber})`, value: phoneNumber }
      }
      const userIdentity = _.get(entity, 'user.additionalIdentities[0]', '')
      if (userIdentity) {
        return {
          label: `${user.data.displayName} (${userIdentity.type} id: ${userIdentity.driverUniqueId})`,
          value: entity.uniqueId
        }
      }
    })

    apis.getContacts().then((historicalSuggestions: any[]) => {
      historicalSuggestions = _.map(historicalSuggestions, (item) => {
        const contactValue = item.email
        if (!contactValue) {
          return
        }
        const suggestionMatch = _.some(suggestions, (suggestion) => valuesMatch(suggestion?.value, contactValue))
        if (suggestionMatch) {
          const matchingSuggestion = _.find(suggestions, (suggestion) => valuesMatch(suggestion?.value, contactValue))
          _.remove(suggestions, (suggestion) => valuesMatch(suggestion?.value, contactValue))
          if (matchingSuggestion) {
            return { label: matchingSuggestion.label, value: contactValue }
          }
        }
        return { label: contactValue, value: contactValue }
      })
      const contacts = _.filter(_.concat(suggestions, historicalSuggestions), (value) => value !== undefined)
      this.setState({ contacts })
    })
  }

  // TODO(Peter): this is crazy but in the share configuration modal this
  // is possible and multiselect just doesn't show those contacts
  private getOptions = () => {
    const { value } = this.props
    const { contacts } = this.state
    const optionsNotInContacts: Array<{ label: string; value: string }> = []
    _.forEach(value, (contact) => {
      if (!_.find(contacts, (item) => valuesMatch(item?.value, contact))) {
        optionsNotInContacts.push({ label: contact, value: contact })
      }
    })
    const combinedOptions = contacts.concat(optionsNotInContacts)
    return combinedOptions.filter((option) => {
      const optionValue = _.get(option, 'value')
      return !_.some(value, (selected) => valuesMatch(optionValue, selected))
    })
  }

  private handleChange = (value: unknown, options: unknown) => {
    const { onChange } = this.props
    if (onChange) {
      onChange(value, options)
    }
  }

  private handleInputChange = (inputValue: string) => {
    const { onInputChange } = this.props
    if (onInputChange) {
      onInputChange(inputValue)
    }
  }

  private renderPrompt(label: string) {
    return `Add recipient "${label}"`
  }

  private handleValidate = (option: string) => {
    return isEmailValid(option) || isPhoneValid(option) || isUUIDValid(option)
  }
}
